/* eslint-disable no-param-reassign */
/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import { set } from 'lodash-es';

import { findDocumentsByPolicyId, findCurrentDocumentsByPolicyId, canRequestPolicyDocuments } from '@/services/api/document/document';
import { getSubscriptionFromId, createEmployeeTravelCertificate } from '@/services/api/buy/subscription';
import { findAllSubscriptionsByCompanyId, findAllCountries, findAllEmployeesByCompanyId } from '@/services/api/customer/company';

let subscriptionsLoadPromise = null;

// initial state
const state = {
  subscriptions: [],
  documents: [],
  employees: [],
  countries: [],
  updateError: null,
  loadedCompanyId: null,
};

// getters
const getters = {
  get: (state) => state.subscriptions
    .filter(({ parentId, status }) => !parentId && !['QUOTED', 'AMENDMENT'].includes(status)),
  certificates: (state) => state.documents,
  employees: (state) => state.employees,
  countries: (state) => state.countries,
  updateError: (state) => state.updateError,
};

function getDocumentPolicyId(subscription) {
  return subscription.policyId || subscription.id;
}

function getCurrentDocumentPolicyId(subscription) {
  return subscription.policyId;
}

function getDocumentId(document) {
  return document.documentId || document.id;
}

function mergeDocuments(docsPayload, currentDocsPayload) {
  const documentsById = new Map();
  docsPayload.forEach((doc) => {
    const documentId = getDocumentId(doc);
    if (documentId) documentsById.set(documentId, doc);
  });

  const mergedDocuments = [...docsPayload];
  currentDocsPayload.forEach((doc) => {
    const documentId = getDocumentId(doc);
    if (!documentId || !documentsById.has(documentId)) {
      mergedDocuments.push(doc);
      return;
    }
    Object.assign(documentsById.get(documentId), doc);
  });
  return mergedDocuments;
}

// actions
const actions = {
  set({ commit }, object) {
    commit('SET', object);
  },
  async updateSubscription({ commit }, subscription) {
    commit('UPDATE_CONTRACT', subscription);
  },
  async setSubscriptions({ commit, rootGetters, state }, { subscriptions: preloadedSubscriptions = null } = {}) {
    const companyId = rootGetters['company/companyId'];

    if (subscriptionsLoadPromise) {
      return subscriptionsLoadPromise;
    }

    if (state.loadedCompanyId === companyId && state.subscriptions.length > 0) {
      return Promise.resolve(state.subscriptions);
    }

    subscriptionsLoadPromise = (async () => {
      const grids = rootGetters['rater/grids'];
      const findGridById = (gridId) => grids.find(({ id }) => id === gridId) || {};
      const subscriptions = (preloadedSubscriptions || (await findAllSubscriptionsByCompanyId(companyId)).data)
        .filter(({ status }) => !['DRAFT', 'AMENDMENT'].includes(status));

      const noDocuments = Promise.resolve({ data: [] });

      const allDocuments = subscriptions.map((subscription) => {
        if (!canRequestPolicyDocuments(subscription.status)) return noDocuments;
        const documentPolicyId = getDocumentPolicyId(subscription);
        return findDocumentsByPolicyId(documentPolicyId);
      });

      const allCurrentDocuments = subscriptions.map((subscription) => {
        if (!canRequestPolicyDocuments(subscription.status)) return noDocuments;
        const currentDocumentPolicyId = getCurrentDocumentPolicyId(subscription);
        return findCurrentDocumentsByPolicyId(currentDocumentPolicyId).catch(() => noDocuments);
      });

      // déplacement pro
      const haveMissionContract = subscriptions.filter(({ status }) => (status === 'ACTIVE' || status === 'SUSPENDED')).filter(({ productId }) => productId === 13).length > 0;
      if (haveMissionContract) {
        const countriesData = await findAllCountries();
        commit('SET', { countries: countriesData.data });

        const employeesData = await findAllEmployeesByCompanyId(companyId);
        commit('SET', { employees: employeesData.data });
      }

      const documents = await Promise.all(allDocuments);
      const currentDocuments = await Promise.all(allCurrentDocuments);
      const subscriptionsWithGrid = subscriptions.map(
        (subscription, index) => {
          const currentDocumentPolicyId = getCurrentDocumentPolicyId(subscription);
          const currentDocsPayload = currentDocuments[index].data || [];
          const docsPayload = documents[index].data || [];
          const hasCurrentDocsPayload = currentDocsPayload.length > 0;
          const currentDocIds = new Set(
            currentDocsPayload.map(getDocumentId).filter(Boolean),
          );
          const sourceDocuments = mergeDocuments(docsPayload, currentDocsPayload);
          const docs = sourceDocuments.map((doc) => Object.assign(doc, {
            current: hasCurrentDocsPayload ? currentDocIds.has(getDocumentId(doc)) : docsPayload.length > 0,
            subscriptionId: doc.subscriptionId || currentDocumentPolicyId,
            documentId: getDocumentId(doc),
            contractId: subscription.id,
            contractActiveVersionId: subscription.activeVersionId,
            contractPolicyId: subscription.policyId,
          }));
          return Object.assign(
            subscription, {
              grid: { ...findGridById(subscription.grid.id) },
              documents: docs,
            },
          );
        },
      );
      const documentsFiltered = subscriptionsWithGrid
        .flatMap((subscription) => (subscription.documents || [])
          .map((document) => Object.assign(document, {
            productId: subscription.productId,
            reference: subscription.reference,
          })));
      commit('SET', { subscriptions: subscriptionsWithGrid, loadedCompanyId: companyId });
      commit('SET', { documents: documentsFiltered });

      return subscriptionsWithGrid;
    })();

    try {
      return await subscriptionsLoadPromise;
    } finally {
      subscriptionsLoadPromise = null;
    }
  },
  async updateContract({ commit }, subscriptionId) {
    if (subscriptionId) {
      const success = await getSubscriptionFromId(subscriptionId);
      commit('UPDATE_CONTRACT', success.data);
      return success;
    }
    return Promise.resolve();
  },
  async createEmployeeTravelCertificate({ commit, rootGetters }, employeeTravelRequest) {
    const compId = rootGetters['company/companyId'];
    const contractId = rootGetters['employee/subscriptionId'];
    Object.assign(employeeTravelRequest, { subscriptionId: contractId });
    Object.assign(employeeTravelRequest,
      {
        employee:
        {
          firstName: employeeTravelRequest.employee.firstName,
          lastName: employeeTravelRequest.employee.lastName,
          email: employeeTravelRequest.employee.email,
          companyId: compId,
        },
      });
    const { data } = await createEmployeeTravelCertificate(contractId, employeeTravelRequest);

    // refresh all employees
    const employeesData = await findAllEmployeesByCompanyId(compId);
    commit('SET', { employees: employeesData.data });

    return data;
  },
  setValue({ commit }, object) {
    commit('SET_VALUE', object);
  },
  setValueWithPath({ commit }, object) {
    commit('SET_VALUE_WITH_PATH', object);
  },
  clear({ commit }) {
    commit('CLEAR');
  },
};

// mutations
const mutations = {
  SET(state, object) {
    const keys = Object.keys(object);
    keys.forEach((key) => {
      // eslint-disable-next-line no-prototype-builtins
      if (state.hasOwnProperty(key)) {
        Object.assign(state, { [key]: object[key] });
      }
    });
  },
  SET_VALUE(state, object) {
    const [key] = Object.keys(object);
    Object.assign(state, { [key]: object[key] });
  },
  SET_VALUE_WITH_PATH(state, { value, path }) {
    set(state, path, value);
  },
  UPDATE_CONTRACT(state, subscription) {
    const sub = state.subscriptions.find((elem) => elem.id === subscription.id);
    if (sub) {
      Object.assign(sub, { ...subscription });
    }
  },
  CLEAR(state) {
    Object.assign(state, {
      subscriptions: [],
      loadedCompanyId: null,
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
